import React from "react"
import logo from "../../assets/images/Logo2.png"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { PATH_MAP } from "../../configuration/PathMap"
import { usePublicContact } from "../../context/usePublicContactContext"

const Footer: React.FC = () => {
  const { t } = useTranslation(["login", "frontend"])

  const { contact } = usePublicContact()

  const hasContact = Boolean(contact.email || contact.whatsAppPhone)

  return (
    <Animate type={AnimationTypes.slideUpIn}>
      <footer className="bg-slate-900 text-slate-400 py-16 pb-8">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className={`grid gap-12 mb-16 ${hasContact ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"}`}>
            <div>
              <div className="flex items-center gap-2 mb-6">
                <Animate type={AnimationTypes.slideRightIn} duration="1.3s">
                  <img src={logo} className="w-[100px]" alt="Logo catalojei" />
                </Animate>
              </div>

              <p className="text-sm leading-relaxed mb-4">{t("label_foot_description")}</p>

              {contact.cnpj && (
                <p className="text-sm">
                  <strong>CNPJ:</strong> {contact.cnpj}
                </p>
              )}
            </div>

            <div>
              <h4 className="text-white font-bold mb-6">{t("label_foot_title")}</h4>

              <ul>
                <li className="mb-4">
                  <a
                    href="/#features"
                    className="text-sm hover:text-red-500 transition-colors"
                    data-interaction-description-key="interactionDescriptions:components.landPage.footer.link.footFeatures"
                  >
                    {t("label_foot_features")}
                  </a>
                </li>

                <li className="mb-4">
                  <a
                    href="/#plans"
                    className="text-sm hover:text-red-500 transition-colors"
                    data-interaction-description-key="interactionDescriptions:components.landPage.footer.link.footPrice"
                  >
                    {t("label_foot_price")}
                  </a>
                </li>

                <li className="mb-4">
                  <Link
                    to={PATH_MAP.PUBLIC_TUTORIALS_PAGE}
                    className="text-sm hover:text-red-500 transition-colors"
                    data-interaction-description-key="interactionDescriptions:components.landPage.footer.link.tutorials"
                  >
                    {t("landpage:label_tutorials")}
                  </Link>
                </li>

                <li className="mb-4">
                  <Link
                    to={PATH_MAP.DIGITAL_MENU_LANDING_PAGE}
                    className="text-sm hover:text-red-500 transition-colors"
                    data-interaction-description-key="interactionDescriptions:components.landPage.footer.link.publicMenu"
                  >
                    {t("landpage:label_public_menu")}
                  </Link>
                </li>

                <li className="mb-4">
                  <Link
                    to={PATH_MAP.RESELLER_LANDING_PAGE}
                    className="text-sm hover:text-red-500 transition-colors"
                    data-interaction-description-key="interactionDescriptions:components.landPage.footer.link.reseller"
                  >
                    {t("landpage:label_be_reseller")}
                  </Link>
                </li>

                <li className="mb-4">
                  <Link
                    to={PATH_MAP.COURIER_PANEL_PAGE}
                    className="text-sm hover:text-red-500 transition-colors"
                    data-interaction-description-key="interactionDescriptions:components.landPage.footer.link.courier"
                  >
                    {t("landpage:label_be_courier")}
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-6">{t("frontend:footer.legal")}</h4>

              <ul>
                <li className="mb-4">
                  <Link
                    to="/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm hover:text-red-500 transition-colors"
                    data-interaction-description-key="interactionDescriptions:components.landPage.footer.link.termsOfService"
                  >
                    {t("frontend:footer.terms_of_service")}
                  </Link>
                </li>

                <li className="mb-4">
                  <Link
                    to="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm hover:text-red-500 transition-colors"
                    data-interaction-description-key="interactionDescriptions:components.landPage.footer.link.privacyPolicy"
                  >
                    {t("frontend:footer.privacy_policy")}
                  </Link>
                </li>

                <li className="mb-4">
                  <Link
                    to="/cancellation-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm hover:text-red-500 transition-colors"
                    data-interaction-description-key="interactionDescriptions:components.landPage.footer.link.cancellationPolicy"
                  >
                    {t("frontend:footer.cancellation_policy")}
                  </Link>
                </li>

                <li className="mb-4">
                  <Link
                    to="/refund-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm hover:text-red-500 transition-colors"
                    data-interaction-description-key="interactionDescriptions:components.landPage.footer.link.refundPolicy"
                  >
                    {t("frontend:footer.refund_policy")}
                  </Link>
                </li>
              </ul>
            </div>

            {hasContact && (
              <div>
                <h4 className="text-white font-bold mb-6">{t("label_foot_contact")}</h4>

                <ul>
                  {contact.emailUrl && (
                    <li className="mb-4">
                      <a
                        href={contact.emailUrl}
                        className="text-sm hover:text-red-500 transition-colors break-all"
                        data-interaction-description-key="interactionDescriptions:components.landPage.footer.link.link"
                      >
                        {contact.email}
                      </a>
                    </li>
                  )}

                  {contact.whatsappUrl && (
                    <li className="mb-4">
                      <a
                        href={contact.whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm hover:text-red-500 transition-colors"
                        data-interaction-description-key="interactionDescriptions:components.landPage.footer.link.whatsApp"
                      >
                        {t("label_support_whatsapp_title")}: {contact.whatsappLabel}
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>

          <div className="pt-8 border-t border-white/5 flex flex-col items-center gap-4 text-center md:flex-row md:justify-between md:text-left">
            <p className="text-xs font-semibold uppercase tracking-wide">
              © {new Date().getFullYear()} catalojei
            </p>

            <p className="text-xs font-semibold uppercase tracking-wide">{t("frontend:footer.all_rights_reserved")}</p>
          </div>
        </div>
      </footer>
    </Animate>
  )
}

export default Footer
